import _ from "lodash";
import { FC, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useCrudEditContext } from "@ryde/redux";
import {
  conditionFieldNamesSF,
  fieldValuesProxySF,
  ICustomField,
  IFormAttributes,
  Log,
  NO_VALUE_DISPLAY,
} from "@ryde/shared";

import { RenderTemplate } from "./_core";

interface IEditFormWrapper {
  controls: ICustomField[];
  injectedFormAttributes?: Partial<IFormAttributes>;
  // Force-disable ALL controls (read-only form) — e.g. a locked past taxi-supplier ride (RYDE-7633).
  disabled?: boolean;
}

export const FormControlsWrapper: FC<IEditFormWrapper> = ({
  controls,
  injectedFormAttributes,
  disabled,
}) => {
  const { getValues } = useFormContext();
  const {
    entity,
    data: { isDuplicate },
  } = useCrudEditContext();

  const controlsComputed = useMemo(() => {
    if (!injectedFormAttributes && !disabled) {
      return controls;
    }

    return controls.map(control => ({
      ...control,
      formAttributes: {
        ...control.formAttributes,
        ...injectedFormAttributes,
        ...(disabled ? { disabled: true } : {}),
      },
    }));
  }, [controls, injectedFormAttributes, disabled]);

  const setFieldsSnapshot = useSetRecoilState(fieldValuesProxySF(entity));
  const conditionFieldNames = useRecoilValue(conditionFieldNamesSF(entity));

  // Subscribe to the condition-driving values only. Watching the whole form here
  // made react-hook-form re-render its root on every keystroke, which re-rendered
  // every control and rebuilt the validation schema for each typed character.
  const conditionValues = useWatch({ name: conditionFieldNames, exact: true });

  useEffect(() => {
    const fieldValues = getValues();
    Log.debug(
      `Field snapshot was set, isDuplicate: ${isDuplicate}, id:${fieldValues?.id || NO_VALUE_DISPLAY}`,
    );

    // Deep clone: react-hook-form mutates its own values object in place, so a
    // shallow copy would let the stored snapshot change underneath Recoil.
    setFieldsSnapshot(_.cloneDeep(fieldValues));
  }, [conditionValues, getValues, isDuplicate, setFieldsSnapshot]);

  return (
    <>
      {controlsComputed?.map((control, index) => {
        return <RenderTemplate {...control} key={control.id + "_" + index} />;
      })}
    </>
  );
};
